import styled from 'styled-components';
import { BookingState, GetWorkPlan6BookingDTO, WorkPlanItemDTO } from '../../../../../api/api';
import { setSelectedTaskId } from '../../../../../stateManagement/reducers/workPlanViewReducer';
import { store } from '../../../../../stateManagement/store';
import { formatDateHourString } from '../../../../../utils/dateHandling';
import Typography from '../../../../../components/typography/Typography';
import { getLocationString } from '../../../../../utils/location/locationHandling';
import { VehicleDriveIcon } from '../../../../../assets/icons/VehicleDriveIcon';
import { StopIcon } from '../../../../../assets/icons/StopIcon';
import Button from '../../../../../components/button/Button';
import { BookingStateBadge } from './BookingStateBadge';
import {useCallback } from 'react';
import TaskService from '../../../../../services/TaskService';
import { log } from '../../../../../utils/logging/log';
import NotificationService from '../../../../../services/NotificationService';
import DayOverviewTaskInBooking from './DayOverviewTaskInBooking';
import { useConfirmationDialog } from '../../../../../hooks/useConfirmationDialog';
import { getEnumDisplayValue } from '../../../../../utils/enumUtils';

interface Props {
  booking: GetWorkPlan6BookingDTO;
  fetchTasks: () => void;
  activeBookingId?: number;
  isFirstBooking?: boolean;
  unfinishedTasks?: WorkPlanItemDTO[];
  isCalendar?: boolean;
  showDoneBookings?: boolean;
}

const handleOnSelectTask = (taskId: number | undefined) => {
    store.dispatch(setSelectedTaskId(taskId));
};


const DayOverviewBooking = (props: Props) => {
    const {booking, isCalendar, fetchTasks} = props;
    const { getConfirmation } = useConfirmationDialog();
    // The backend only allows driving to the next booking. On that booking we keep "Kør" visible but
    // greyed out and explain the reason on click; on the rest the button is not shown at all.
    const isDriveAllowed = booking.isDriveAllowed ?? false;
    const showDriveButton = booking.state === BookingState.Planned && (isDriveAllowed || (props.isFirstBooking ?? false));
    

  const handleDriveClick = useCallback(async () => {
    TaskService.driveToBooking(booking.id as number) //TODO: Hvorfor er alt optional?
      .catch((error) => {
        log(error);
        NotificationService.error('Der skete en fejl. Kunne ikke ændre bookingstatus.');
      })
      .then(() => {
        fetchTasks(); //TODO: Reloader hele siden, det der ingen grund til, den skal "bare" reload booking
        })

  }, [booking]);

  const handleCloseBooking = useCallback(async () => {
    // The backend decides whether the booking may be closed. When it may not we still show the
    // dialog, but only to explain why - there is no way to close the booking anyway.
    if (!booking.isBookingClosable) {
      const dialog = await TaskService.closeBookingDialog(booking.id!)
      const unfinishedTasks = dialog.unfinishedTasks ?? [];
      const taskList = unfinishedTasks.map(t => `- <b>${t.taskTypeName}</b> - ${t.workTaskId}<br>&nbsp;&nbsp;&nbsp;<b>Første mand:</b> ${t.technicalResponsible?.name ?? 'Ingen'}`).join('<br><br>');
      await getConfirmation(
        {
          headerText: 'Bookingen kan ikke lukkes',
          bodyText: `Der er følgende ikke-afsluttede opgaver:

 ${taskList}

 Du skal sætte dine egne opgaver på pause eller afslutte dem, før du kan lukke bookingen.
`,
          confirmButtonText: undefined,
          declineButtonText: 'OK'
        },
        true
      );

      return;
    }
    TaskService.leaveBooking(booking.id as number) //TODO: Hvorfor er alt optional?
      .catch((error) => {
        log(error);
        NotificationService.error('Der skete en fejl. Kunne ikke ændre bookingstatus.');
      })
      .then(() => {
        fetchTasks(); //TODO: Reloader hele siden, det der ingen grund til, den skal "bare" reload booking
      })
  }, [booking]);

  const handleDriveNotAllowed = useCallback(async () => {
    const unfinishedTasks = props.unfinishedTasks ?? [];
    const taskList = unfinishedTasks
      .map(t => `<li><b>${t.taskType?.name ?? ''} - ${t.workTaskId}</b> - ${t.status ? getEnumDisplayValue(t.status) : ''}</li>`)
      .join('');
    await getConfirmation(
      {
        headerText: 'Du kan ikke køre til bookingen',
        bodyText: `Du skal have afsluttet alle Uafsluttede opgaver før du kan køre på denne booking.${taskList ? `<ul>${taskList}</ul>` : ''}`,
        confirmButtonText: undefined,
        declineButtonText: 'OK'
      },
      true
    );
  }, [props.unfinishedTasks]);

  const handleFindWay = useCallback(async (e?: any) => {
      e && e.stopPropagation();
      // Only claim the booking when the tech is actually allowed to drive to it. Otherwise "Find vej"
      // is pure navigation and must not change the booking state.
      if(booking.state == BookingState.Planned && isDriveAllowed) {
          await handleDriveClick()
      }
      const latLngUrl =
          'https://www.google.com/maps/dir/?api=1&destination=' +
          booking.location?.latitude +
          ',' +
          booking.location?.longitude;
      window.open(latLngUrl, '_top');
  }, [booking]);

  return <BookingContainer>
        <BookingHeader>
            <Typography variant="h4" fontWeight="bold">
                Booking - {booking.workOrderNumber}<BookingStateBadge state={booking.state} isFixedAppointment={booking.isAppointmentFixed} />
            </Typography>
            <SelectedTime>
                <Typography variant="h4">
                    {booking.appointmentStart && booking.appointmentEnd
                        ? `${formatDateHourString(booking.appointmentStart)}\xa0- ${formatDateHourString(
                            booking.appointmentEnd
                        )}` : ''}
                </Typography>
            </SelectedTime>
        </BookingHeader>
        <BookingDashedBorder>
            <BookingInfo>
                <BookingAddress>
                    <AddressLabel>Adresse</AddressLabel>
                    {getLocationString(booking?.location)}
                </BookingAddress>
                {
                    !isCalendar &&
                    <BookingButtons>
                        {!props.activeBookingId && showDriveButton && (
                            <Button onClick={isDriveAllowed ? handleDriveClick : handleDriveNotAllowed} disabledAppearance={!isDriveAllowed}>
                                <VehicleDriveIcon size="30px" />
                                Kør
                            </Button>
                        )}
                        {(booking.state === BookingState.OnGoing || booking.state === BookingState.OnRoute) && (
                            <Button onClick={handleCloseBooking} variant={booking.isClosePrimary ? 'primary' : 'secondary'}>
                                <StopIcon size="16px" />
                                Luk booking
                            </Button>
                        )}
                      {(isDriveAllowed || booking.state === BookingState.OnRoute) && (
                        <Button variant="secondary" onClick={handleFindWay}>
                          Find vej
                        </Button>
                      )}
                    </BookingButtons>
                }
            </BookingInfo>


            {booking.workTasks?.map(task =>
                <DayOverviewTaskInBooking
                    key={task.workTaskId}
                    handleSelect={handleOnSelectTask}
                    task={task}
                    fetchTasks={fetchTasks}
                    isCalendar={isCalendar}
                    loadingWorkPlanOrders={false}
                    workPlanOrders={[]}
                    booking={booking}
                />)
            }
        </BookingDashedBorder>
    </BookingContainer>
}

const BookingDashedBorder = styled.div`
  border: 3px dashed #bdbdbd;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  align-items: stretch;
  //margin: 24px 12px 24px 12px;
  //padding: 32px;
  border-radius: 12px;
`
const BookingInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 12px 12px 0 12px;
`;
const AddressLabel = styled.div`
  font-weight: bold;
  margin-right: 16px;
`;
const BookingAddress = styled.div`
  display: flex;
`;
const BookingButtons = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 12px;
`;
const BookingContainer = styled.div`
  display: flex;
  justify-content: start;
  flex-direction: column;
  margin: 12px;
`;
const BookingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SelectedTime = styled.span`
  display: inline-flex;
  margin-left: 12px;
`;
export default DayOverviewBooking